import React from 'react';

import { Box } from '@atlaskit/primitives/compiled';

import { type ProductKeys } from '../../../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../../../common/constants/schemas/3p-config';
import { BulkConnectBanner as BulkConnect } from '../../../../../common/ui/bulk-connect-banner';
import { serve3PBulkConnectWithoutExposure } from '../../../../../common/utils/experiments';
import { isHello } from '../../../../../common/utils/is-hello';
import { OAuthContainer } from '../../../../../common/utils/oauth-container';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import {
	useBulkConnectModalOpenState,
	useNewlyConnectedProducts,
} from '../../../../../controllers/bulk-connect';
import { ErrorBoundary } from '../../../../../controllers/error-boundary';
import { useOAuthHandlers } from '../../../../../controllers/oauth';

export type BulkConnectBannerProps = {
	cloudId: string | undefined;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	selectedProduct?: ProductKeys;
	availableProducts: ProductKeys[];
};

export const BulkConnectBanner = ({
	cloudId,
	thirdPartyConfigs,
	selectedProduct,
	availableProducts,
}: BulkConnectBannerProps): React.JSX.Element | null => {
	const { fireAnalyticsScreenEvent, fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const { getNewlyConnectedProducts } = useNewlyConnectedProducts();

	if (!selectedProduct) {
		if (serve3PBulkConnectWithoutExposure()) {
			return (
				<ErrorBoundary section="SearchPageRelayResultsBulkConnectBanner">
					<Box role="listitem">
						<BulkConnect
							thirdPartyConfigs={thirdPartyConfigs}
							availableProducts={availableProducts}
							getNewlyConnectedProducts={getNewlyConnectedProducts}
							useBulkConnectModalOpenState={useBulkConnectModalOpenState}
							fireAnalyticsScreenEvent={fireAnalyticsScreenEvent}
							fireAnalyticsEvent={fireAnalyticsEvent}
							commonAttributes={commonAttributes}
						/>
					</Box>
				</ErrorBoundary>
			);
		}
		return (
			<ErrorBoundary section="SearchPageRelayResultsOAuthContainer">
				<Box role="listitem">
					<OAuthContainer
						thirdPartyConfigs={thirdPartyConfigs}
						availableProducts={availableProducts}
						isHello={isHello(cloudId)}
						useOAuthHandlers={useOAuthHandlers}
						fireAnalyticsEvent={fireAnalyticsEvent}
					/>
				</Box>
			</ErrorBoundary>
		);
	}

	return null;
};
